import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { FilterDatePicker } from '../ui/date-picker';
import { CheckCircle2, XCircle, Clock, FileText, Download, Eye, Search, Calendar, Filter, TrendingUp, ArrowRight, ArrowUpCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { format } from 'date-fns';
import Pagination from '../common/Pagination';
import ExportModal from '../ExportModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [plans, setPlans] = useState([]);
  
  // Tab state - 'all', 'upgrades', 'new_purchases'
  const [activeTab, setActiveTab] = useState('all');
  
  // Upgrade metrics
  const [upgradeMetrics, setUpgradeMetrics] = useState(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPlans, setSelectedPlans] = useState([]);
  
  // Debounce ref for search
  const searchTimeoutRef = useRef(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportCount, setExportCount] = useState(0);

  // Fetch plans for filter dropdown
  useEffect(() => {
    fetchPlans();
    fetchUpgradeMetrics();
  }, []);

  // Fetch logs when filters change (except search which uses debounce)
  useEffect(() => {
    fetchLogs();
  }, [statusFilter, dateFrom, dateTo, selectedPlans, currentPage, itemsPerPage, activeTab]);
  
  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchLogs();
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/admin/plans`);
      setPlans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchUpgradeMetrics = async () => {
    try {
      const response = await axios.get(`${API}/admin/upgrade-metrics`);
      setUpgradeMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch upgrade metrics:', error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (selectedPlans.length > 0) params.append('plan_ids', selectedPlans.join(','));
      
      // Add tab filter
      if (activeTab === 'upgrades') params.append('is_upgrade', 'true');
      if (activeTab === 'new_purchases') params.append('is_upgrade', 'false');
      
      params.append('page', currentPage);
      params.append('per_page', itemsPerPage);
      
      const response = await axios.get(`${API}/admin/payment-logs?${params.toString()}`);
      setLogs(response.data.items || response.data);
      setTotalItems(response.data.total || (Array.isArray(response.data) ? response.data.length : 0));
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-green-600 text-white">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Paid
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-600 text-white">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-orange-500 text-white">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </Badge>
        );
      case 'expired':
        return (
          <Badge className="bg-gray-500 text-white">
            <Clock className="w-3 h-3 mr-1" />
            Expired
          </Badge>
        );
      case 'created':
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-600">
            <Clock className="w-3 h-3 mr-1" />
            Awaiting Payment
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get payment type badge (NP = New Purchase, UP = Upgrade)
  const getPaymentTypeBadge = (log) => {
    if (log.is_upgrade) {
      return (
        <Badge className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5">
          UP
        </Badge>
      );
    }
    return (
      <Badge className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5">
        NP
      </Badge>
    );
  };

  // Format amount - handle both paise and rupees
  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    // If amount is in paise (> 100), convert to rupees
    const rupees = amount > 100 ? amount / 100 : amount;
    return `₹${rupees.toFixed(2)}`;
  };

  const exportToCSV = () => {
    // Open export modal instead of direct export
    setShowExportModal(true);
  };

  // Export handlers
  const fetchExportCount = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFrom) params.append('start_date', dateFrom);
      if (dateTo) params.append('end_date', dateTo);
      
      const response = await axios.get(`${API}/export/payments/count?${params}`);
      setExportCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch export count:', error);
    }
  };

  const handleExport = async (format, activeFilters, selectedColumns) => {
    try {
      setExporting(true);
      
      const params = new URLSearchParams();
      params.append('format', format);
      // Use current page filters
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFrom) params.append('start_date', dateFrom);
      if (dateTo) params.append('end_date', dateTo);
      if (selectedColumns.length > 0) params.append('columns', selectedColumns.join(','));
      
      const response = await axios.get(`${API}/export/payments?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments_export.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export completed successfully');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Fetch export count when export modal opens
  useEffect(() => {
    if (showExportModal) {
      fetchExportCount();
    }
  }, [showExportModal, statusFilter, dateFrom, dateTo]);

  const handlePlanFilterChange = (planId) => {
    setSelectedPlans(prev => {
      if (prev.includes(planId)) {
        return prev.filter(id => id !== planId);
      } else {
        return [...prev, planId];
      }
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setSelectedPlans([]);
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // Group plans by grade for the filter
  const plansByGrade = plans.reduce((acc, plan) => {
    const grade = plan.grade || 'All Grades';
    if (!acc[grade]) acc[grade] = [];
    acc[grade].push(plan);
    return acc;
  }, {});

  // Format currency helper
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  return (
    <div className="space-y-6">
      {/* Upgrade Metrics - Show when on Upgrades tab */}
      {activeTab === 'upgrades' && upgradeMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Upgrades</p>
                  <p className="text-2xl font-bold text-purple-600">{upgradeMetrics.total_upgrades || 0}</p>
                </div>
                <ArrowUpCircle className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Upgrade Revenue</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(upgradeMetrics.upgrade_revenue || 0)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Rebates Given</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(upgradeMetrics.total_rebates || 0)}</p>
                </div>
                <RefreshCw className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Top Upgrade Path</p>
                  <p className="text-sm font-bold text-blue-600 truncate" title={upgradeMetrics.top_upgrade_path || 'N/A'}>
                    {upgradeMetrics.top_upgrade_path || 'N/A'}
                  </p>
                  {upgradeMetrics.top_upgrade_count > 0 && (
                    <p className="text-xs text-gray-400">{upgradeMetrics.top_upgrade_count} upgrades</p>
                  )}
                </div>
                <ArrowRight className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Payment Logs
              </CardTitle>
              <CardDescription>
                View and manage all payment transactions
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {/* Legend */}
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <Badge className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5">NP</Badge>
                  <span className="text-gray-500">New Purchase</span>
                </div>
                <div className="flex items-center gap-1">
                  <Badge className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5">UP</Badge>
                  <span className="text-gray-500">Upgrade</span>
                </div>
              </div>
              <Button onClick={exportToCSV} variant="outline" size="sm" className="border-green-600 text-green-600 hover:bg-green-50">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b">
            <button
              onClick={() => handleTabChange('all')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'all' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              All Payments
            </button>
            <button
              onClick={() => handleTabChange('upgrades')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                activeTab === 'upgrades' 
                  ? 'border-purple-600 text-purple-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ArrowUpCircle className="w-4 h-4" />
              Upgrades
            </button>
            <button
              onClick={() => handleTabChange('new_purchases')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'new_purchases' 
                  ? 'border-green-600 text-green-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              New Purchases
            </button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Filters Section */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {(statusFilter !== 'all' || searchQuery || dateFrom || dateTo || selectedPlans.length > 0) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-blue-600 h-6">
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, INDoS, or order ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <div>
                <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setCurrentPage(1); }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="created">Awaiting Payment</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Plan Filter */}
              <div>
                <Select 
                  value={selectedPlans.length > 0 ? 'selected' : 'all'}
                  onValueChange={(value) => {
                    if (value === 'all') setSelectedPlans([]);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue>
                      {selectedPlans.length > 0 ? `${selectedPlans.length} Plan(s)` : 'All Plans'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    {Object.entries(plansByGrade).map(([grade, gradePlans]) => (
                      <div key={grade}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-100">{grade}</div>
                        {gradePlans.map(plan => (
                          <div
                            key={plan.id}
                            className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-gray-50"
                            onClick={(e) => { e.stopPropagation(); handlePlanFilterChange(plan.id); }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPlans.includes(plan.id)}
                              onChange={() => {}}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{plan.name}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date Range */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Date Range:</span>
              </div>
              <div className="flex items-center gap-2">
                <FilterDatePicker
                  date={dateFrom ? new Date(dateFrom) : null}
                  onDateChange={(date) => { setDateFrom(date ? format(date, 'yyyy-MM-dd') : ''); setCurrentPage(1); }}
                  placeholder="From date"
                  className="w-40"
                />
                <span className="text-gray-400">to</span>
                <FilterDatePicker
                  date={dateTo ? new Date(dateTo) : null}
                  onDateChange={(date) => { setDateTo(date ? format(date, 'yyyy-MM-dd') : ''); setCurrentPage(1); }}
                  placeholder="To date"
                  className="w-40"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>User Details</TableHead>
                  <TableHead>Transaction / Order</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-16">
                      <div className="flex flex-col items-center gap-2">
                        <div className="spinner"></div>
                        <span className="text-gray-500">Loading payment logs...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : !logs || logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-16">
                      <div className="flex flex-col items-center gap-2">
                        <FileText className="w-12 h-12 text-gray-300" />
                        <p className="text-gray-500">No payment logs found</p>
                        {(searchQuery || statusFilter !== 'all' || dateFrom || dateTo) && (
                          <p className="text-sm text-gray-400">Try adjusting your filters</p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} className={log.is_upgrade ? 'bg-purple-50/30' : ''}>
                      <TableCell className="text-sm">
                        <div>{new Date(log.created_at).toLocaleDateString()}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(log.created_at).toLocaleTimeString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getPaymentTypeBadge(log)}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{log.user_name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">{log.user_indos || log.user_email || 'N/A'}</div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const txn = log.razorpay_payment_id || log.payment_id;
                          const ord = log.order_id || log.razorpay_order_id;
                          return (
                            <div className="text-xs font-mono text-gray-700 space-y-0.5" data-testid="payment-log-ids">
                              {txn ? (
                                <div title={txn} className="truncate max-w-[180px]">
                                  <span className="text-[10px] uppercase tracking-wide text-emerald-700 mr-1">Txn</span>
                                  {txn}
                                </div>
                              ) : (
                                <div className="text-[10px] italic text-gray-400">Txn pending</div>
                              )}
                              {ord ? (
                                <div title={ord} className="truncate max-w-[180px] text-gray-500">
                                  <span className="text-[10px] uppercase tracking-wide text-gray-400 mr-1">Order</span>
                                  {ord}
                                </div>
                              ) : null}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatAmount(log.amount)}
                        {log.is_upgrade && log.rebate_amount > 0 && (
                          <div className="text-xs text-green-600 font-normal">
                            (-{formatAmount(log.rebate_amount)} rebate)
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {log.plan_name || log.plan_type || 'N/A'}
                        </Badge>
                        {log.is_upgrade && log.from_plan_name && (
                          <div className="text-xs text-gray-500 mt-1">
                            from: {log.from_plan_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(log.status)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedLog(log);
                            setShowDetailsDialog(true);
                          }}
                          className="h-8 px-2"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(val) => {
                  setItemsPerPage(val);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Transaction Details
              {selectedLog && (
                <span className="ml-2">
                  {selectedLog.is_upgrade ? (
                    <Badge className="bg-purple-600 text-white">Upgrade Payment</Badge>
                  ) : (
                    <Badge className="bg-blue-600 text-white">New Purchase</Badge>
                  )}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4 py-4">
              {/* Status Banner */}
              <div className={`p-3 rounded-lg ${
                selectedLog.status === 'paid' ? 'bg-green-50 border border-green-200' :
                selectedLog.status === 'failed' ? 'bg-red-50 border border-red-200' :
                'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Status</span>
                  {getStatusBadge(selectedLog.status)}
                </div>
              </div>

              {/* Upgrade Plan Transition (only for upgrades) */}
              {selectedLog.is_upgrade && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-purple-800">Plan Upgrade</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 p-3 bg-white rounded-lg border border-purple-200">
                      <p className="text-xs text-gray-500 mb-1">Previous Plan</p>
                      <p className="font-medium text-gray-800">{selectedLog.from_plan_name || 'N/A'}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <div className="flex-1 p-3 bg-purple-600 text-white rounded-lg">
                      <p className="text-xs text-purple-200 mb-1">New Plan</p>
                      <p className="font-medium">{selectedLog.plan_name || 'N/A'}</p>
                    </div>
                  </div>
                  {selectedLog.days_credited > 0 && (
                    <p className="text-xs text-purple-600 mt-2">
                      {selectedLog.days_credited} days credited from previous plan
                    </p>
                  )}
                </div>
              )}

              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">User Name</p>
                  <p className="font-medium">{selectedLog.user_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">INDoS Number</p>
                  <p className="font-medium">{selectedLog.user_indos || 'N/A'}</p>
                </div>
              </div>

              {/* Plan & Price Breakdown */}
              <div className={`rounded-lg p-4 space-y-3 ${selectedLog.is_upgrade ? 'bg-purple-50' : 'bg-blue-50'}`}>
                <div className="flex justify-between items-center">
                  <span className={`font-medium ${selectedLog.is_upgrade ? 'text-purple-800' : 'text-blue-800'}`}>
                    {selectedLog.is_upgrade ? 'New Plan' : 'Plan'}
                  </span>
                  <span className="font-medium">{selectedLog.plan_name || selectedLog.plan_type || 'N/A'}</span>
                </div>
                
                {/* Price Breakdown */}
                <div className={`border-t pt-3 space-y-2 ${selectedLog.is_upgrade ? 'border-purple-200' : 'border-blue-200'}`}>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {selectedLog.is_upgrade ? 'New Plan Price' : 'Original Price'}
                    </span>
                    <span>{selectedLog.original_price ? formatAmount(selectedLog.original_price) : formatAmount(selectedLog.amount)}</span>
                  </div>
                  
                  {/* Rebate (for upgrades) */}
                  {selectedLog.is_upgrade && selectedLog.rebate_amount > 0 && (
                    <div className="flex justify-between text-sm bg-green-100 p-2 rounded">
                      <span className="text-green-700 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        Credit from previous plan
                        {selectedLog.days_credited > 0 && (
                          <span className="text-xs">({selectedLog.days_credited} days)</span>
                        )}
                      </span>
                      <span className="text-green-700 font-medium">
                        -{formatAmount(selectedLog.rebate_amount)}
                      </span>
                    </div>
                  )}
                  
                  {/* Coupon Discount */}
                  {selectedLog.coupon_code && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        Coupon ({selectedLog.coupon_code})
                      </span>
                      <span className="text-green-600">
                        -{selectedLog.coupon_discount ? formatAmount(selectedLog.coupon_discount) : '₹0.00'}
                      </span>
                    </div>
                  )}
                  
                  {/* Coins Discount */}
                  {selectedLog.coins_redeemed > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        Coins Redeemed ({selectedLog.coins_redeemed} coins)
                      </span>
                      <span className="text-green-600">
                        -{selectedLog.coins_discount ? formatAmount(selectedLog.coins_discount) : '₹0.00'}
                      </span>
                    </div>
                  )}
                  
                  {/* Final Amount */}
                  <div className={`flex justify-between font-bold text-lg border-t pt-2 mt-2 ${selectedLog.is_upgrade ? 'border-purple-200' : 'border-blue-200'}`}>
                    <span>Amount Paid</span>
                    <span className={selectedLog.is_upgrade ? 'text-purple-700' : 'text-blue-700'}>
                      {formatAmount(selectedLog.amount)}
                    </span>
                  </div>
                </div>
              </div>

              {/* IDs */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Order ID</p>
                  <p className="font-mono text-sm break-all">{selectedLog.order_id || selectedLog.razorpay_order_id || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Payment ID</p>
                  <p className="font-mono text-sm break-all">{selectedLog.razorpay_payment_id || selectedLog.payment_id || 'N/A'}</p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Payment Method</p>
                  <p className="font-medium capitalize">{selectedLog.payment_method || selectedLog.method || 'Online Payment'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Currency</p>
                  <p className="font-medium">{selectedLog.currency || 'INR'}</p>
                </div>
              </div>

              {/* Plan Duration (for both) */}
              {selectedLog.plan_duration_days && (
                <div>
                  <p className="text-sm text-gray-500">Plan Duration</p>
                  <p className="font-medium">{selectedLog.plan_duration_days} days</p>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Created At</p>
                  <p>{new Date(selectedLog.created_at).toLocaleString()}</p>
                </div>
                {selectedLog.paid_at && (
                  <div>
                    <p className="text-gray-500">Paid At</p>
                    <p>{new Date(selectedLog.paid_at).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* New Expiry (for successful payments) */}
              {selectedLog.new_expiry && selectedLog.status === 'paid' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-gray-500">New Plan Expiry</p>
                  <p className="font-medium text-green-700">
                    {new Date(selectedLog.new_expiry).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}

              {/* Error Message */}
              {selectedLog.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-800">Error Message</p>
                  <p className="text-sm text-red-600 mt-1">{selectedLog.error_message}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <ExportModal
        open={showExportModal}
        onOpenChange={setShowExportModal}
        title="Export Payment Logs"
        description="Export payment logs based on current filters. Only filtered data will be exported."
        filters={[]}
        columns={[
          { key: 'user_name', label: 'User Name' },
          { key: 'user_email', label: 'User Email' },
          { key: 'user_indos', label: 'INDoS' },
          { key: 'amount', label: 'Amount (₹)' },
          { key: 'currency', label: 'Currency' },
          { key: 'status', label: 'Status' },
          { key: 'plan_name', label: 'Plan' },
          { key: 'plan_duration_days', label: 'Duration (Days)' },
          { key: 'payment_id', label: 'Payment ID' },
          { key: 'order_id', label: 'Order ID' },
          { key: 'payment_method', label: 'Method' },
          { key: 'coupon_code', label: 'Coupon' },
          { key: 'discount_amount', label: 'Discount' },
          { key: 'created_at', label: 'Date' }
        ]}
        onExport={handleExport}
        exporting={exporting}
        totalCount={exportCount}
      />
    </div>
  );
};

export default PaymentLogs;
